<script setup>
import { onMounted, ref, inject, computed } from 'vue'
import app from '@/main.js'
import { title } from 'radash'
import InfoExtProfileItems from '@/components/Creator/Content/Info/Includes/InfoExtProfile/Includes/InfoExtProfileItems/InfoExtProfileItems.vue'
import dayjs from '@/plugins/dayjsPluginNext.js'

const errorMessage = ref('')
const extendedProfile = ref({})
const creator = inject('creator')

onMounted(async () => {
  if (!creator.userId) {
    return
  }

  try {
    const { data } = await app.wsp
      .sendRequest({
        data: {
          userId: creator.userId
        },
        method: 'extendedProfile.get'
      })

    extendedProfile.value = data.extendedProfile || {}
  } catch (error) {
    errorMessage.value = error.message || 'An error occurred while fetching extended profile data.'
  }
})

const details = computed(() => ({
  age: {
    value: creator.birthDate ? dayjs().diff(dayjs(creator.birthDate, 'YYYY-MM-DD'), 'year') : null,
    title: app.$i18n.t('creator.profile.age'), // Age
    bySubscription: true
  },
  birthDate: {
    value: creator.birthDate ? dayjs(creator.birthDate).format('D MMMM YYYY') : null, // YYYY-MM-DD
    title: app.$i18n.t('creator.profile.birth_date'), // 'Date of Birth'
    bySubscription: true
  },
  bornIn: {
    value: extendedProfile.value.bornIn,
    title: app.$i18n.t('creator.profile.born_in'), // 'Born in',
    bySubscription: true
  },
  liveIn: {
    value: creator.location.name,
    title: app.$i18n.t('creator.profile.live_in') // 'Live in'
  },
  speaks: {
    value: creator.languages.join(', '),
    title: app.$i18n.t('creator.profile.speaks') // Speaks
  },
  nativeLanguage: {
    value: extendedProfile.value.nativeLanguage,
    title: app.$i18n.t('creator.profile.native_language')
  }
}))

const basicInfo = computed(() => ({
  heightInCm: {
    value: extendedProfile.value.heightInCm,
    title: app.$i18n.t('creator.profile.height') // 'Height (cm)'
  },
  weightInKg: {
    value: extendedProfile.value.weightInKg,
    title: app.$i18n.t('creator.profile.weight') // 'Weight (kg)'
  },
  bodyType: {
    value: extendedProfile.value.bodyType,
    title: app.$i18n.t('creator.profile.body_type') // 'Body type'
  },
  maritalStatus: {
    value: extendedProfile.value.maritalStatus,
    title: app.$i18n.t('creator.profile.marital_status'), // 'Marital status',
    bySubscription: true
  },
  children: {
    value: extendedProfile.value.children ? 'Yes' : 'No',
    title: app.$i18n.t('creator.profile.has_children'), // 'Has children',
    bySubscription: true
  }
}))

const preferences = computed(() => ({
  favoriteAnimals: {
    value: extendedProfile.value.favoriteAnimals,
    title: app.$i18n.t('creator.profile.favorite_animals') // 'Favorite animals'
  },
  favoriteArtists: {
    value: extendedProfile.value.favoriteArtists,
    title: app.$i18n.t('creator.profile.favorite_celebrities') // 'Favorite celebrities'
  },
  favoriteBooks: {
    value: extendedProfile.value.favoriteBooks,
    title: app.$i18n.t('creator.profile.favorite_books') // 'Favorite books'
  },
  favoriteColor: {
    value: extendedProfile.value.favoriteColor,
    title: app.$i18n.t('creator.profile.favorite_color') // 'Favorite color'
  },
  favoriteEmojis: {
    value: extendedProfile.value.favoriteEmojis,
    title: app.$i18n.t('creator.profile.favorite_emojis') // 'Favorite emojis'
  },
  favoriteFilms: {
    value: extendedProfile.value.favoriteFilms,
    title: app.$i18n.t('creator.profile.favorite_films') // 'Favorite films'
  },
  favoriteGames: {
    value: extendedProfile.value.favoriteGames,
    title: app.$i18n.t('creator.profile.favorite_games') // 'Favorite games'
  },
  hobbies: {
    value: extendedProfile.value.hobbies,
    title: app.$i18n.t('creator.profile.hobbies') // 'Hobbies'
  },
  alcohol: {
    value: extendedProfile.value.alcohol,
    title: app.$i18n.t('creator.profile.alcohol'), // 'Alcohol',
    bySubscription: true
  },
  smoking: {
    value: extendedProfile.value.smoking,
    title: app.$i18n.t('creator.profile.smoking'), // 'Smoking',
    bySubscription: true
  }
}))

const aboutMe = computed(() => ({
  zodiacSign: {
    value: title(extendedProfile.value.zodiacSign),
    title: app.$i18n.t('creator.profile.zodiac_sign'), // 'Zodiac sign',
    bySubscription: true
  },
  profession: {
    value: extendedProfile.value.profession,
    title: app.$i18n.t('creator.profile.profession') // 'Profession'
  },
  religion: {
    value: title(extendedProfile.value.religion),
    title: app.$i18n.t('creator.profile.religion'), // 'Religion',
    bySubscription: true
  },
  tattoo: {
    value: extendedProfile.value.tattoo,
    title: app.$i18n.t('creator.profile.tattoos'), // 'Tattoos',
    bySubscription: true
  },
  pets: {
    value: extendedProfile.value.pets,
    title: app.$i18n.t('creator.profile.pets') // 'Pets'
  },
  sports: {
    value: extendedProfile.value.sports,
    title: app.$i18n.t('creator.profile.sports') // 'Sports'
  },
  iCantLiveWithout: {
    value: extendedProfile.value.iCantLiveWithout,
    title: app.$i18n.t('creator.profile.i_cant_live_without'), // 'I can’t live without',
    bySubscription: true
  },
  iHaveBeenTo: {
    value: extendedProfile.value.iHaveBeenTo,
    title: app.$i18n.t('creator.profile.i_have_been_to') // 'I Have Been To'
  },
  inMyFreeTimeI: {
    value: extendedProfile.value.inMyFreeTimeI,
    title: app.$i18n.t('creator.profile.in_my_free_time') // 'In my free time I'
  },
  funFactAboutMe: {
    value: extendedProfile.value.funFactAboutMe,
    title: app.$i18n.t('creator.profile.fun_fact_about_me'), // 'Fun fact about me',
    bySubscription: true
  }
}))
</script>

<template>
  <div class="space-y-6">
    <div>
      <h4 class="text-base font-bold mb-4">
        {{ $t('creator.profile.details') }}
      </h4>

      <InfoExtProfileItems :items="details"/>
    </div>

    <div>
      <h4 class="text-base font-bold mb-4">
        {{ $t('creator.profile.basic_info') }}
      </h4>

      <InfoExtProfileItems :items="basicInfo"/>
    </div>

    <div>
      <h4 class="text-base font-bold mb-4">
        {{ $t('creator.profile.preferences') }}
      </h4>

      <InfoExtProfileItems :items="preferences"/>
    </div>

    <div>
      <h4 class="text-base font-bold mb-4">
        {{ $t('creator.profile.about_me') }}
      </h4>

      <InfoExtProfileItems :items="aboutMe"/>
    </div>
  </div>
</template>
